import type { SyntheticEvent } from "react";
import { Link } from "react-router-dom";
import { resolveHeaderBranding } from "../../config/branding";
import { useAuth } from "../../context/AuthContext";
import { useTenantSlug } from "../../context/TenantSlugContext";
import { buildTenantPath } from "../../utils/tenantSlug";
import type { Client } from "../../services/UserService";
import { useClinicProfile } from "../../context/ClinicProfileContext";

interface HeaderBrandLogoProps {
  className?: string;
}

const HeaderBrandLogo = ({ className = "" }: HeaderBrandLogoProps) => {
  const { selectedClient } = useAuth();
  const { resolvedTenant } = useTenantSlug();
  const { effectiveTenantSlug, profile, isTenantContext } = useClinicProfile();
  const tenantFromSlug: Client | null = resolvedTenant
    ? {
        clientId: resolvedTenant.clientId,
        clientName: resolvedTenant.clientName,
        clientSlug: resolvedTenant.clientSlug,
        userRole: "admin",
      }
    : null;
  const branding = resolveHeaderBranding(selectedClient || tenantFromSlug);
  const tenantDisplayName = profile?.displayName || branding.tenantName;
  const tenantLogoSrc = profile?.logoWordmarkUrl || branding.logoSrc;
  const tenantLogoAlt = profile?.logoAlt || branding.logoAlt;
  const hasCustomTenantLogo =
    isTenantContext && Boolean(profile?.logoWordmarkUrl || (branding.context === "tenant" && !branding.showTenantBadge));
  const useHorizontalLockup = !isTenantContext || !hasCustomTenantLogo;

  const handleLogoError = (event: SyntheticEvent<HTMLImageElement>) => {
    event.currentTarget.src = "/logos/revahub-mark.svg";
  };

  return (
    <Link to={buildTenantPath("/", effectiveTenantSlug)} className={`flex items-center ${className}`.trim()}>
      {useHorizontalLockup ? (
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={branding.poweredByMarkSrc}
            alt="Revahub"
            className="h-12 w-12 sm:h-14 sm:w-14 object-contain shrink-0"
            onError={handleLogoError}
          />
          <div className="leading-tight min-w-0">
            <span className="block text-xl sm:text-2xl font-display font-bold text-primary-700">
              Reva <span className="text-violet-500">Hub</span>
            </span>
            {isTenantContext ? (
              <span className="block text-xs sm:text-sm font-medium text-neutral-700 truncate max-w-[180px]">
                {tenantDisplayName}
              </span>
            ) : (
              <span className="block text-[11px] sm:text-xs text-neutral-500">
                Plataforma multi-tenant
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={tenantLogoSrc}
            alt={tenantLogoAlt}
            onError={handleLogoError}
            className="h-16 sm:h-20 w-auto max-w-[260px] object-contain object-left"
          />
          <div className="hidden lg:flex flex-col leading-tight">
            <span className="text-sm font-semibold text-neutral-700">{tenantDisplayName}</span>
            <span className="flex items-center gap-1 text-xs text-neutral-500">
              Powered by
              <img
                src={branding.poweredByMarkSrc}
                alt="Revahub"
                className="h-3.5 w-3.5 object-contain"
                onError={handleLogoError}
              />
            </span>
          </div>
        </div>
      )}
    </Link>
  );
};

export default HeaderBrandLogo;
